import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Box,
  Button,
  Table as MuiTable,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { useEffect, useMemo } from 'react'
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage'
import { HEADER_HEIGHT } from 'src/components/header/Header'
import { RevalidationSpinner } from 'src/components/revalidationSpinner/RevalidationSpinner'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { Spinner } from 'src/components/spinner/Spinner'
import { sessionStorageKeys } from 'src/constants/sessionStorageKeys'
import { ScheduledReportSummaryWrapper } from 'src/features/scheduledReportSummary/ScheduledReportSummaryWrapper'
import { useQueryScheduledReportSummariesWithDaysInfo } from 'src/features/scheduledReportSummary/api/useQueryScheduledReportSummariesWithDaysInfo'
import { DayHeaderCell } from 'src/features/scheduledReportSummary/components/DayHeaderCell'
import { FiltersPopover } from 'src/features/scheduledReportSummary/components/FiltersPopover'
import { LimitedRangePicker } from 'src/features/scheduledReportSummary/components/LimitedRangePicker'
import { ScheduleNameSearchInput } from 'src/features/scheduledReportSummary/components/ScheduleNameSearchInput'
import { StatusCell } from 'src/features/scheduledReportSummary/components/StatusCell'
import { dropInaccessiblePlaceNodeUuids } from 'src/features/scheduledReportSummary/dropInaccessiblePlaceNodeUuids'
import { useSelectablePlaceNodes } from 'src/features/scheduledReportSummary/hooks/useSelectablePlaceNodes'
import { toFriendlyTsv } from 'src/features/scheduledReportSummary/toFriendlyTsv'
import type { ScheduledReportSummary as ScheduledReportSummaryType } from 'src/features/scheduledReportSummary/types'
import { useFilters } from 'src/features/scheduledReportSummary/useFilters'
import { useScrollRestorer } from 'src/hooks/useScrollRestorer'
import { checkIfToday } from 'src/util/datetime/checkIfToday'
import { formatToJapaneseDate } from 'src/util/datetime/formatToJapaneseDate'

export const maxSelectableDays = 7

const dayHeaderColumnWidth = 88
const placeNameColumnWidth = 184
const scheduleNameColumnWidth = 264

// useScrollRestorer の querySelector ターゲット
const SCROLL_CLASS = 'js-scheduled-report-summary-scroll'

const stickyPlaceNameSx = {
  bgcolor: 'white',
  left: 0,
  overflow: 'hidden',
  position: 'sticky',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: placeNameColumnWidth,
  zIndex: 1,
} as const

const stickyScheduleNameSx = {
  bgcolor: 'white',
  left: placeNameColumnWidth,
  overflow: 'hidden',
  position: 'sticky',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: scheduleNameColumnWidth,
  zIndex: 1,
} as const

export const ScheduledReportSummary = () => {
  useScrollRestorer(
    `.${SCROLL_CLASS}`,
    sessionStorageKeys.uiScheduledReportSummariesScrollY,
  )

  const { showSuccessSnackbar } = useSnackbar()

  const { filters, isSomeFilterInDrawerActive, setFilterOf, resetFilterOf } =
    useFilters()

  const selectablePlaceNodes = useSelectablePlaceNodes()

  // URL に残った選択できない現場を絞り込みから除外する
  const accessiblePlaceNodeUuids = useMemo(
    () =>
      dropInaccessiblePlaceNodeUuids(
        filters.placeNodeUuids,
        selectablePlaceNodes.placeNodes.map(placeNode => placeNode.uuid),
      ),
    [filters.placeNodeUuids, selectablePlaceNodes.placeNodes],
  )

  useEffect(() => {
    if (accessiblePlaceNodeUuids.length === filters.placeNodeUuids.length) {
      return
    }
    setFilterOf.placeNodeUuids(accessiblePlaceNodeUuids, {
      replaceHistory: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setFilterOf は毎レンダー作り直されるため依存に含めない
  }, [accessiblePlaceNodeUuids, filters.placeNodeUuids])

  // scheduleNameKeyword はクライアントサイド絞り込み用のため、API リクエストには渡さない。
  // queryKey からも除外されることは queryKeys.ts 側でランタイム保証されている。
  const summariesQuery = useQueryScheduledReportSummariesWithDaysInfo({
    dateRange: filters.dateRange,
    placeNodeUuids: accessiblePlaceNodeUuids,
  })

  const { targetDates = [], summaries = [] } = summariesQuery.data || {}

  // スケジュール名フィルタはクライアントサイドで部分一致適用する
  const filteredSummaries = useMemo(() => {
    const keyword = filters.scheduleNameKeyword.trim()
    if (keyword === '') return summaries
    return summaries.filter((s: ScheduledReportSummaryType) =>
      s.scheduleName.includes(keyword),
    )
  }, [summaries, filters.scheduleNameKeyword])

  // 現場の取得に失敗すると一覧取得も実行されないため、ローディング表示のままにせずエラーを出す
  if (summariesQuery.isError || selectablePlaceNodes.isError) {
    return (
      <ScheduledReportSummaryWrapper withPadding>
        <ErrorMessage>エラーが発生しました</ErrorMessage>
      </ScheduledReportSummaryWrapper>
    )
  }

  const copyDataIntoClipboard = async () => {
    if (summariesQuery.isPending) return
    // 画面に表示されているフィルタ後の内容をコピーする
    const tsv = toFriendlyTsv({
      targetDates: summariesQuery.data.targetDates,
      summaries: filteredSummaries,
    })
    await navigator.clipboard.writeText(tsv)
    showSuccessSnackbar('クリップボードにコピーしました', {
      autoHideDuration: 1500,
    })
  }

  return (
    <ScheduledReportSummaryWrapper>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        sx={{ height: '64px', px: 2 }}
      >
        <Stack alignItems="center" direction="row" spacing={2}>
          <FiltersPopover
            filters={filters}
            isSomeFilterActive={isSomeFilterInDrawerActive}
            resetFilterOf={resetFilterOf}
            setFilterOf={setFilterOf}
          />
          <ScheduleNameSearchInput
            onChange={setFilterOf.scheduleNameKeyword}
            value={filters.scheduleNameKeyword}
          />
          <LimitedRangePicker
            key={filters.dateRange?.join('-') ?? ''}
            onChange={range => {
              if (range === null) {
                resetFilterOf.dateRange()
              } else {
                setFilterOf.dateRange(range[0], range[1])
              }
            }}
            value={filters.dateRange}
          />
        </Stack>
        <Button
          disabled={summariesQuery.isPending}
          onClick={copyDataIntoClipboard}
          startIcon={<ContentCopyIcon />}
          variant="outlined"
          color="secondary"
        >
          データをコピー
        </Button>
      </Stack>

      <Box
        sx={{
          bgcolor: 'white',
          // 余っている高さをすべて占有したい
          height: `calc(100vh - var(--banner-height, 0px) - ${HEADER_HEIGHT + 64}px)`,
          position: 'relative',
        }}
      >
        {summariesQuery.isPending && (
          // キャッシュがない場合(≒初回のデータ取得時)に表示される、画面をブロックするタイプのスピナー
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.7)',
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              left: 0,
              position: 'absolute',
              right: 0,
              top: 0,
              zIndex: 1,
            }}
          >
            <Spinner />
          </Box>
        )}
        <TableContainer
          className={SCROLL_CLASS}
          sx={{
            // 行エリアだけを縦スクロール可能にしたい
            height: `calc(100vh - var(--banner-height, 0px) - ${72 + 64 + 39}px)`,
          }}
        >
          <MuiTable
            size="small"
            stickyHeader
            sx={{
              // 列幅を th の width で固定するために必要
              tableLayout: 'fixed',
              minWidth: placeNameColumnWidth + scheduleNameColumnWidth,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    left: 0,
                    position: 'sticky',
                    width: placeNameColumnWidth,
                    zIndex: 3,
                  }}
                >
                  現場名
                </TableCell>
                <TableCell
                  sx={{
                    left: placeNameColumnWidth,
                    position: 'sticky',
                    width: scheduleNameColumnWidth,
                    zIndex: 3,
                  }}
                >
                  スケジュール名
                </TableCell>
                {targetDates.map(day => (
                  <TableCell key={day} sx={{ width: dayHeaderColumnWidth }}>
                    <DayHeaderCell isActive={checkIfToday(day)}>
                      {formatToJapaneseDate(day)}
                    </DayHeaderCell>
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSummaries.map(row => (
                <TableRow key={`${row.placeNodeUuid}-${row.scheduleUuid}`}>
                  <TableCell sx={stickyPlaceNameSx}>{row.placeName}</TableCell>
                  <TableCell sx={stickyScheduleNameSx}>
                    {row.scheduleName}
                  </TableCell>
                  {row.dailyDetails.map((detail, i) => (
                    <TableCell key={targetDates[i]} sx={{ p: 0 }}>
                      <StatusCell
                        reportUuid={detail.reportUuid}
                        status={detail.status}
                      />
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </TableContainer>
      </Box>
      {
        // キャッシュがある場合(≒2回目以降のデータ取得時)に表示される、画面をブロックしないタイプのスピナー
        summariesQuery.isRefetching && <RevalidationSpinner />
      }
    </ScheduledReportSummaryWrapper>
  )
}
